import React from 'react'

/**
 * The hero backdrop: a hand-rolled canvas of slow teal embers drifting up
 * through the dark, with an occasional horizontal scan-sweep — the engine
 * room's instrument light. Pure rAF, no dependencies.
 *
 * Degrades by design:
 *  - prefers-reduced-motion OR small screens (<768px) OR no 2d context →
 *    renders nothing; the CSS `.mkt3-hero-glow` gradient underneath carries
 *    the composition on its own.
 *  - pauses when the tab is hidden.
 * Purely decorative: aria-hidden, pointer-events-none.
 */

interface Particle {
  x: number
  y: number
  r: number
  vy: number
  sway: number
  phase: number
  alpha: number
}

const PARTICLE_COUNT = 64
const SWEEP_PERIOD_MS = 9000
const SWEEP_DURATION_MS = 2600

export default function EngineRoomCanvas({ className }: { className?: string }) {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null)
  const [enabled, setEnabled] = React.useState(false)

  React.useEffect(() => {
    // Require a real browser (matchMedia present — jsdom lacks it), a large
    // screen, and no reduced-motion preference; otherwise the CSS glow carries.
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const small = window.innerWidth < 768
    setEnabled(!reduced && !small)
  }, [])

  React.useEffect(() => {
    if (!enabled) return
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let width = 0
    let height = 0
    let raf = 0
    let running = true
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const particles: Particle[] = []

    const resize = () => {
      const rect = canvas.parentElement?.getBoundingClientRect()
      width = rect?.width ?? window.innerWidth
      height = rect?.height ?? window.innerHeight
      canvas.width = Math.round(width * dpr)
      canvas.height = Math.round(height * dpr)
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }

    const seed = () => {
      particles.length = 0
      for (let i = 0; i < PARTICLE_COUNT; i++) {
        particles.push({
          x: Math.random() * width,
          y: Math.random() * height,
          r: 0.6 + Math.random() * 1.7,
          vy: 0.08 + Math.random() * 0.3,
          sway: 6 + Math.random() * 18,
          phase: Math.random() * Math.PI * 2,
          alpha: 0.05 + Math.random() * 0.3,
        })
      }
    }

    const start = performance.now()

    const frame = (now: number) => {
      if (!running) return
      ctx.clearRect(0, 0, width, height)
      const t = (now - start) / 1000

      // Embers drifting upward with a slow sine sway
      for (const p of particles) {
        p.y -= p.vy
        if (p.y < -6) {
          p.y = height + 6
          p.x = Math.random() * width
        }
        const x = p.x + Math.sin(t * 0.4 + p.phase) * p.sway
        const twinkle = 0.75 + 0.25 * Math.sin(t * 1.4 + p.phase * 3)
        ctx.beginPath()
        ctx.arc(x, p.y, p.r, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(45, 212, 191, ${(p.alpha * twinkle).toFixed(3)})`
        ctx.fill()
      }

      // Occasional scan sweep — a faint instrument pass down the panel
      const cycle = (now - start) % SWEEP_PERIOD_MS
      if (cycle < SWEEP_DURATION_MS) {
        const progress = cycle / SWEEP_DURATION_MS
        const y = progress * (height + 160) - 80
        const grad = ctx.createLinearGradient(0, y - 60, 0, y + 60)
        grad.addColorStop(0, 'rgba(45, 212, 191, 0)')
        grad.addColorStop(0.5, `rgba(45, 212, 191, ${(0.05 * Math.sin(progress * Math.PI)).toFixed(3)})`)
        grad.addColorStop(1, 'rgba(45, 212, 191, 0)')
        ctx.fillStyle = grad
        ctx.fillRect(0, y - 60, width, 120)
        // the bright edge of the sweep
        ctx.fillStyle = `rgba(45, 212, 191, ${(0.12 * Math.sin(progress * Math.PI)).toFixed(3)})`
        ctx.fillRect(0, y, width, 1)
      }

      raf = requestAnimationFrame(frame)
    }

    const onVisibility = () => {
      if (document.hidden) {
        running = false
        cancelAnimationFrame(raf)
      } else if (!running) {
        running = true
        raf = requestAnimationFrame(frame)
      }
    }

    resize()
    seed()
    raf = requestAnimationFrame(frame)
    window.addEventListener('resize', resize)
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      running = false
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', resize)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [enabled])

  if (!enabled) return null

  return <canvas ref={canvasRef} aria-hidden="true" className={className} style={{ pointerEvents: 'none' }} />
}
